import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { type IWizardStep, bindAction } from "interfaces";
import { useCallback, useEffect } from "react";
import { type ConnectedProps, connect } from "react-redux";
import { reset, setCustomersToAdd } from "../state/TemplateAvailableCustomersSlice";
import { getAvailableCustomers, saveCustomers } from "../state/thunks/TemplateAvailableCustomersThunk";
import { useEventCallback } from "hooks/useEventCallback";
import { GroupTemplateCustomers } from "../Details/GroupTemplateCustomers";
import { createColumnOptions } from "../Details/Customers/getColumnOptions";
import { Button } from "components/buttons/button/Button";
import { RouteNames } from "enums";
import { useNavigate } from "react-router";

type Props = IWizardStep & PropsFromRedux;

export const CreateCustomers = (props: Props) => {
  const {
    acceptHandlers,
    saveCustomers,
    groupId,
    selectedIds,
    setSelectedIds,
    items,
    isLoading,
    itemsCount,
    getData,
    reset,
  } = props;

  const navigate = useNavigate();

  const saveCustomersCb = useEventCallback(async () => {
    await saveCustomers(groupId);
    reset();
  }, [groupId, reset, saveCustomers]);

  useEffect(() => {
    acceptHandlers?.({
      onClose: reset,
      onFinish: saveCustomersCb,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderAddCustomersButton = useCallback(() => {
    if (itemsCount > 0) {
      return null;
    }
    return (
      <Button primary className={"temp"} onClick={() => navigate(`/${RouteNames.linkedAccountsManagement}/create`)}>
        Create Customer
      </Button>
    );
  }, [navigate, itemsCount]);

  return (
    <GroupTemplateCustomers
      getData={getData}
      items={items}
      isLoading={isLoading}
      itemsCount={itemsCount}
      selectedIds={selectedIds}
      setSelectedIds={setSelectedIds}
      columnOptions={createColumnOptions}
      renderAddCustomersButton={renderAddCustomersButton}
    />
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    groupId: state.people.groupTemplate.base.groupId,
    items: state.people.groupTemplate.availableCustomers.items,
    itemsCount: state.people.groupTemplate.availableCustomers.itemsCount,
    isLoading: state.people.groupTemplate.availableCustomers.isLoading,
    selectedIds: state.people.groupTemplate.availableCustomers.customersToAdd,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    setSelectedIds: (ids: number[]) => dispatch(setCustomersToAdd(ids)),
    getData: bindAction(getAvailableCustomers, dispatch),
    saveCustomers: (templateId: number) => dispatch(saveCustomers(templateId)),
    reset: () => dispatch(reset()),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(CreateCustomers);
export default ConnectedComponent;
